import { useCallback, useMemo } from 'react';
import { P, type PermissionKey } from './permission-keys';
import { usePermissionsStore } from './permission-store';

/**
 * Core permission hook.
 *
 * Reads the resolved permission map from the rbac permission store and
 * exposes simple check helpers. The map is populated on login via the
 * permission matrix API (wiring lives in the service layer / app shell).
 */
export function usePermission() {
  const loaded = usePermissionsStore((s) => s.loaded);
  const basePermissions = usePermissionsStore((s) => s.basePermissions);
  const projectPermissions = usePermissionsStore((s) => s.projectPermissions);
  const permissions = usePermissionsStore((s) => s.permissions);
  const sources = usePermissionsStore((s) => s.sources);
  const activeProjectId = usePermissionsStore((s) => s.activeProjectId);

  /** Check if the user has a specific permission (merged: org + active project scope). */
  const can = useCallback(
    (key: PermissionKey): boolean => {
      if (!loaded) return false;
      return permissions[key] === true;
    },
    [loaded, permissions],
  );

  /** Check if the user has ALL of the listed permissions (merged). */
  const canAll = useCallback(
    (keys: PermissionKey[]): boolean => {
      if (!loaded) return false;
      return keys.every((k) => permissions[k] === true);
    },
    [loaded, permissions],
  );

  /** Check if the user has ANY of the listed permissions (merged). */
  const canAny = useCallback(
    (keys: PermissionKey[]): boolean => {
      if (!loaded) return false;
      return keys.some((k) => permissions[k] === true);
    },
    [loaded, permissions],
  );

  /**
   * Check a permission against org-level base permissions only.
   * Use this for global UI elements (sidebar nav, top-level feature gates)
   * that must not be affected by project-scoped permission elevation.
   */
  const canBase = useCallback(
    (key: PermissionKey): boolean => {
      if (!loaded) return false;
      return basePermissions[key] === true;
    },
    [loaded, basePermissions],
  );

  /** Check if user has ANY of the keys at org level (base only). */
  const canAnyBase = useCallback(
    (keys: PermissionKey[]): boolean => {
      if (!loaded) return false;
      return keys.some((k) => basePermissions[k] === true);
    },
    [loaded, basePermissions],
  );

  /**
   * Check a permission for a specific project without switching the active context.
   * Uses cached project permissions from the single API call.
   * Falls back to base permissions if the project has no cached data.
   */
  const canForProject = useCallback(
    (key: PermissionKey, projectId: string): boolean => {
      if (!loaded) return false;
      const projPerms = projectPermissions[projectId];
      if (!projPerms) return basePermissions[key] === true;
      // Project-level permissions override base when the key exists in project map
      if (key in projPerms.permissions) return projPerms.permissions[key] === true;
      return basePermissions[key] === true;
    },
    [loaded, basePermissions, projectPermissions],
  );

  /**
   * True when ANY project the user can access resolves the key. For global entry
   * points to project-scoped data, which are empty unless some project grants it.
   */
  const canAnyProject = useCallback(
    (key: PermissionKey): boolean => {
      if (!loaded) return false;
      return Object.values(projectPermissions).some((proj) => proj.permissions[key] === true);
    },
    [loaded, projectPermissions],
  );

  /**
   * Get granular permission mode for a project tab (view/create/edit/delete/manage).
   * Uses the currently active merged permissions (org + project after switchProject).
   */
  const tabPermissions = useMemo(() => {
    if (!loaded) return null;
    const p = permissions;
    return {
      modules: {
        canView: p[P.HIERARCHY_MODULE_VIEW] === true,
        canCreate: p[P.HIERARCHY_MODULE_CREATE] === true,
        canEdit: p[P.HIERARCHY_MODULE_EDIT] === true,
        canDelete: p[P.HIERARCHY_MODULE_DELETE] === true,
      },
      features: {
        canView: p[P.HIERARCHY_FEATURE_VIEW] === true,
        canCreate: p[P.HIERARCHY_FEATURE_CREATE] === true,
        canEdit: p[P.HIERARCHY_FEATURE_EDIT] === true,
        canDelete: p[P.HIERARCHY_FEATURE_DELETE] === true,
      },
      tasks: {
        canView: p[P.HIERARCHY_TASK_STATUS] === true || p[P.HIERARCHY_TASK_DELETE_OWN] === true,
        canCreate: p[P.HIERARCHY_TASK_CREATE] === true,
        canAssign: p[P.HIERARCHY_TASK_ASSIGN] === true,
        canEdit: p[P.HIERARCHY_TASK_STATUS] === true,
        canDelete: p[P.HIERARCHY_TASK_DELETE] === true,
        canDeleteOwn: p[P.HIERARCHY_TASK_DELETE_OWN] === true,
      },
      meetings: {
        canView: p[P.MEETINGS_VIEW] === true,
        canViewAll: p[P.MEETINGS_VIEW_ALL] === true,
        canCreate: p[P.MEETINGS_CREATE] === true,
        canEdit: p[P.MEETINGS_EDIT] === true,
        canDelete: p[P.MEETINGS_DELETE] === true,
        canCancel: p[P.MEETINGS_CANCEL] === true,
        canEnableClara: p[P.MEETINGS_CLARA_ENABLE] === true,
      },
      team: {
        canView: p[P.PROJECTS_VIEW_TEAM] === true,
        canAdd: p[P.PROJECTS_TEAM_ADD] === true,
        canEdit: p[P.PROJECTS_TEAM_EDIT] === true,
        canRemove: p[P.PROJECTS_TEAM_REMOVE] === true,
        canManage:
          p[P.PROJECTS_TEAM_ADD] === true ||
          p[P.PROJECTS_TEAM_EDIT] === true ||
          p[P.PROJECTS_TEAM_REMOVE] === true,
      },
      settings: {
        canView: p[P.SETTINGS_PROJECT_VIEW] === true,
        canManage: p[P.SETTINGS_PROJECT_CONFIG] === true,
      },
      financials: {
        canView: p[P.PROJECTS_VIEW_FINANCIALS] === true,
      },
    };
  }, [loaded, permissions]);

  return {
    loaded,
    permissions,
    projectPermissions,
    sources,
    activeProjectId,
    can,
    canAll,
    canAny,
    canBase,
    canAnyBase,
    canForProject,
    canAnyProject,
    tabPermissions,
  };
}
